// LiveRegion — announces async-state text to screen readers without
// moving visual focus.
//
// Two priorities:
//   - polite   (default)  → role="status",  aria-live="polite"
//   - assertive           → role="alert",   aria-live="assertive"
//
// Polite announcements wait for the current utterance to finish and are
// appropriate for "Saving…" / "Template saved" transitions. Assertive
// announcements interrupt and are reserved for errors that require
// immediate attention.
//
// The region is always present in the DOM so assistive tech sees text
// *changes* rather than a node insertion (which can be missed on some
// engines). When `status` is empty we render an empty region — the
// region itself stays mounted, but there is no text to announce. We
// also set `aria-hidden="true"` in the empty case so AT does not even
// acknowledge the region exists until we have something to say.
//
// Visually hidden via the standard "sr-only" style: AT reads it, sighted
// users never see it.

export type LiveRegionPriority = 'polite' | 'assertive'

export interface LiveRegionProps {
  status: string
  priority?: LiveRegionPriority
}

const SR_ONLY_CLASSES =
  'absolute h-px w-px overflow-hidden whitespace-nowrap border-0 p-0 [clip:rect(0,0,0,0)]'

export function LiveRegion({
  status,
  priority = 'polite',
}: LiveRegionProps) {
  const isEmpty = status === ''
  const role = priority === 'assertive' ? 'alert' : 'status'
  const ariaLive = priority === 'assertive' ? 'assertive' : 'polite'
  return (
    <div
      role={role}
      aria-live={ariaLive}
      aria-atomic="true"
      aria-hidden={isEmpty ? 'true' : undefined}
      className={SR_ONLY_CLASSES}
    >
      {status}
    </div>
  )
}
